import { Tooltip } from '@mui/material';
import { dateFormatter, limitDecimals, lrpDateFormat, pages } from 'common';
import {
  InsureIQCoverageStatus,
  isRmaLrpTypeCode,
  getLrpCommodityInfo,
  LgmCommodityInfo,
  InsureIQPolicyTypes,
  RmaLrpCommodityNameByCode,
  RmaLgmCommodityNameByCode,
  isRmaLrpCommodityCode,
  isRmaLgmCommodityCode,
  isRmaLgmTypeCode,
} from '@harvestiq/constants';
import { dayjs, formatCurrency, formatNumber } from '@harvestiq/utils';
import { getEntityName } from '@harvestiq/iiq-common';
import { EndorsementStatusChip } from '../components/EndorsementStatusChip/EndorsementStatusChip';
import { InfoTooltip } from '../components/InfoTooltip';

const tooltipTitleMap: Record<string, [string, string]> = {
  // First index is expected/projected and second index is actual
  endingValue: ['Expected Ending Value', 'Actual Ending Value'],
  indemnity: ['Projected Indemnity', 'Actual Indemnity'],
  grossMargin: ['Projected Gross Margin', 'Actual Gross Margin'],
  grossMarginChange: [
    'Projected Gross Margin Change',
    'Actual Gross Margin Change',
  ],
};

export const getToolTipTitle = (
  key: string,
  value: unknown
): string | undefined => {
  const isActual = value !== null && value !== undefined;
  if (isActual) {
    return tooltipTitleMap[key]?.[1]; // index 1
  }
  return tooltipTitleMap[key]?.[0]; // index 0
};

export const calculationTypes = {
  cwt: 'cwt',
  head: 'head',
  total: 'total',
} as const;

export type CalculationType =
  (typeof calculationTypes)[keyof typeof calculationTypes];

export const priceToggleOptions = {
  cash: 'cash',
  futures: 'futures',
};

const projectedIndemnityTooltipTitle =
  'Projected Indemnity is estimated given current market conditions and does not guarantee a specific outcome.';

type MarketingMonth = {
  marketingMonth: string;
  numberofHead: number;
  premium: number;
  grossMarginGuarantee: number;
  grossMargin: number | null;
  actualGrossMargin: number | null;
  grossMarginChange: number | null;
  actualGrossMarginChange: number | null;
  [key: string]: unknown;
};

type ColumnDef = {
  field?: string;
  id?: string;
  label: React.ReactNode;
  isSortable?: boolean;
  render?: (value: unknown, row: Record<string, unknown>) => React.ReactNode;
};

const lgmCoverageSubTableColumns: ColumnDef[] = [
  { field: 'marketingMonth', label: 'Marketing Month' },
  { field: 'numberofHead', label: 'Number of Head' },
  {
    field: 'premium',
    label: 'Producer Premium',
    render: (monthPremium) => formatCurrency(monthPremium as number),
  },
  {
    field: 'grossMarginGuarantee',
    label: 'Gross Margin Guarantee',
    render: (monthGrossMarginGuarantee) =>
      formatCurrency(monthGrossMarginGuarantee as number),
  },
  {
    field: 'grossMargin',
    label: 'Gross Margin',
    render: (monthGrossMargin, { actualGrossMargin }) => (
      <Tooltip title={getToolTipTitle('grossMargin', actualGrossMargin)}>
        <span>{formatCurrency(monthGrossMargin as number)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'grossMarginChange',
    label: (
      <InfoTooltip
        title={
          'Gross Margin Change is estimated given current market conditions and does not guarantee a specific outcome.'
        }
      >
        <span>Gross Margin Change</span>
      </InfoTooltip>
    ),
    render: (monthGrossMarginChange, { actualGrossMarginChange }) => {
      // NOTE: The math behind gross margin change is correct, the sign is correct calculation wise, but for ensuring better UX we are inversing the signal.
      const adjustedSignalMonthGrossMarginChange =
        (monthGrossMarginChange as number) * -1;

      return (
        <Tooltip
          title={getToolTipTitle('grossMarginChange', actualGrossMarginChange)}
        >
          <span>
            {formatCurrency(adjustedSignalMonthGrossMarginChange, {
              signDisplay: 'always',
            })}
          </span>
        </Tooltip>
      );
    },
  },
];

export const calculationOptionsByPolicyType: Record<string, CalculationType[]> =
  {
    [InsureIQPolicyTypes.LRP]: [
      calculationTypes.cwt,
      calculationTypes.head,
      calculationTypes.total,
    ],
    [InsureIQPolicyTypes.LGM]: [calculationTypes.head, calculationTypes.total],
  };

export const calculatorsByCalculationType: Record<
  string,
  (targetWeight?: number, numberOfHeads?: number) => (value: number) => number
> = {
  cwt: () => (value) => limitDecimals(value),

  head: (targetWeight = 1) => {
    return (value) => {
      return limitDecimals(value * targetWeight);
    };
  },

  total: (targetWeight = 1, numberOfHeads = 1) => {
    return (value) => {
      return limitDecimals(numberOfHeads * targetWeight * value);
    };
  },
};

const calculateMarketingMonthValues = (
  marketingMonth: MarketingMonth,
  calculationType: CalculationType
) => {
  const {
    subsidy,
    premium,
    actualIndemnity,
    projectedIndemnity,
    actualGrossMargin,
    projectedGrossMargin,
    numberofHead,
    grossMarginGuarantee,
    actualGrossMarginChange,
    projectedGrossMarginChange,
  } = marketingMonth as MarketingMonth & Record<string, number | null>;

  const calculator = calculatorsByCalculationType[calculationType](
    1,
    numberofHead
  );

  return {
    ...marketingMonth,
    subsidy: calculator(subsidy!),
    premium: calculator(premium),
    // Ensure that indemnity is never displayed as a negative value on a monthly basis
    indemnity: Math.max(
      0,
      calculator((actualIndemnity ?? projectedIndemnity)!)
    ),
    grossMarginChange: calculator(
      (actualGrossMarginChange ?? projectedGrossMarginChange)!
    ),
    grossMargin: calculator((actualGrossMargin ?? projectedGrossMargin)!),
    grossMarginGuarantee: calculator(grossMarginGuarantee),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Coverage = Record<string, any>;

const calculateLgmValues = (coverage: Coverage, _calculationType: string) => {
  const { deductible } = coverage;

  return {
    deductible,
    numberofHead: coverage.targetMarketingsTotal,
  };
};

export const restructureCoverages = (
  coverages: Coverage[] = [],
  calculationType: string = calculationTypes.head
): Coverage[] => {
  return coverages.map((rawCoverage) => {
    const coverage = { ...rawCoverage };

    if (coverage.type === InsureIQPolicyTypes.LGM) {
      Object.assign(coverage, calculateLgmValues(coverage, calculationType));
    } else {
      let coverageValuesUom: Record<string, unknown> = {};
      switch (calculationType) {
        case calculationTypes.cwt:
          coverageValuesUom = {
            premium: coverage.producerPremiumPerCwt,
            cashIndemnity: coverage.projectedCashIndemnityPerCwt,
            futuresIndemnity: coverage.projectedFuturesIndemnityPerCwt,
            actualIndemnity: coverage.actualIndemnityPerCwt,
            endingValue:
              coverage.actualEndingValuePerCwt ??
              coverage.expectedEndingValuePerCwt,
            cashPrice: coverage.projectedCashEndingValuePerCwt,
            futuresPrice: coverage.projectedFuturesEndingValuePerCwt,
            actualEndingValue: coverage.actualEndingValuePerCwt,
            coveragePrice: coverage.coveragePricePerCwt,
          };
          break;
        case calculationTypes.head:
          coverageValuesUom = {
            premium: coverage.producerPremiumPerHead,
            cashIndemnity: coverage.projectedCashIndemnityPerHead,
            futuresIndemnity: coverage.projectedFuturesIndemnityPerHead,
            actualIndemnity: coverage.actualIndemnityPerHead,
            endingValue:
              coverage.actualEndingValuePerHead ??
              coverage.expectedEndingValuePerHead,
            cashPrice: coverage.projectedCashEndingValuePerHead,
            futuresPrice: coverage.projectedFuturesEndingValuePerHead,
            actualEndingValue: coverage.actualEndingValuePerHead,
            coveragePrice: coverage.coveragePricePerHead,
          };
          break;

        default:
          coverageValuesUom = {
            premium: coverage.producerPremiumTotal,
            cashIndemnity: coverage.projectedCashIndemnityTotal,
            futuresIndemnity: coverage.projectedFuturesIndemnityTotal,
            actualIndemnity: coverage.actualIndemnityTotal,
            endingValue:
              coverage.actualEndingValueTotal ??
              coverage.expectedEndingValueTotal,
            cashPrice: coverage.projectedCashEndingValueTotal,
            futuresPrice: coverage.projectedFuturesEndingValueTotal,
            actualEndingValue: coverage.actualEndingValueTotal,
            coveragePrice: coverage.coveragePriceTotal,
          };
          break;
      }

      Object.assign(coverage, {
        ...coverageValuesUom,
        numberofHead: coverage.headCount,
      });
    }

    return { ...coverage };
  });
};

export const statusColumn: ColumnDef = {
  id: 'status',
  label: 'Status',
  isSortable: true,
  render: (status, { externalStatus, isExternallyManaged }) => {
    return (
      <EndorsementStatusChip
        status={status as InsureIQCoverageStatus}
        isExternallyManaged={isExternallyManaged as boolean}
        externalStatus={externalStatus as string | null}
      />
    );
  },
};

export const ownersColumn: ColumnDef = {
  id: 'owner',
  label: 'Entity Name',
  render: (_, { entityHistory }) => {
    return getEntityName(entityHistory as Parameters<typeof getEntityName>[0]);
  },
};

export const commodityColumn: ColumnDef = {
  id: 'commodityCode',
  label: 'Commodity',
  isSortable: true,
  render: (commodityCode, { typeCode }) => {
    const code = commodityCode as string;
    const tCode = typeCode as string;
    if (isRmaLrpCommodityCode(code) && isRmaLrpTypeCode(tCode)) {
      const commodityType = getLrpCommodityInfo({
        commodityCode: code,
        typeCode: tCode,
      }).hiqTypeCodeName;
      const commodityName = RmaLrpCommodityNameByCode[code];
      return `${commodityName} - ${commodityType}`;
    }

    if (isRmaLgmCommodityCode(code) && isRmaLgmTypeCode(tCode)) {
      const commodityType = LgmCommodityInfo[tCode].hiqTypeCodeName;
      const commodityName = RmaLgmCommodityNameByCode[code];
      return `${commodityName} - ${commodityType}`;
    }

    return '';
  },
};

export const salesEffectiveDateColumn: ColumnDef = {
  id: 'salesEffectiveDate',
  label: 'Sales Effective Date',
  render: (salesEffectiveDate) =>
    dateFormatter(salesEffectiveDate as string, lrpDateFormat),
};

export const endDateColumn: ColumnDef = {
  id: 'endDate',
  label: (
    <InfoTooltip title="Specific Coverage Endorsement End Date indicates when the given LRP Position will close.">
      <span>SCE End Date</span>
    </InfoTooltip>
  ),
};

export const deductibleColumn: ColumnDef = {
  id: 'deductible',
  label: 'Deductible',
  render: (deductible) => formatCurrency(deductible as number),
};

export const targetWeightColumn: ColumnDef = {
  id: 'targetWeightQuantityCwt',
  label: 'Target Weight (cwt)',
  render: (targetWeightQuantityCwt) => {
    return targetWeightQuantityCwt
      ? (targetWeightQuantityCwt as { toNumber: () => number }).toNumber()
      : '-';
  },
};

export const headCountColumn: ColumnDef = {
  id: 'numberofHead',
  label: 'Total Number of Head',
  render: (headCount) =>
    headCount ? formatNumber(headCount as number, 0) : '-',
};

export const premiumColumn: ColumnDef = {
  id: 'premium',
  label: 'Premium',
  render: (premium) => formatCurrency(premium as number),
};

export const coveragePriceColumn: ColumnDef = {
  id: 'coveragePrice',
  label: 'Coverage Price',
  render: (coveragePrice) => {
    return coveragePrice ? formatCurrency(coveragePrice as number) : '-';
  },
};

export const endingValueColumn: ColumnDef = {
  id: 'endingValue',
  label: (
    <InfoTooltip
      title={
        <>
          Latest relevant cash value. See the{' '}
          <a
            href={`${pages.faq.path}?faqs=what-are-the-calculations-for-actual-ending-value-and-expected-ending-value`}
            target="_blank"
            rel="noreferrer"
          >
            FAQ
          </a>{' '}
          for more detail.
        </>
      }
    >
      <span>Cash Price</span>
    </InfoTooltip>
  ),
  render: (endingValue) => {
    return <span>{formatCurrency(endingValue as number)}</span>;
  },
};

type CashPriceTooltipArgs = {
  cashSymbol?: string;
  cashTimestamp?: string | null;
  commodity?: string;
};

export const generateCashPriceTooltip = ({
  cashSymbol,
  cashTimestamp,
  commodity,
}: CashPriceTooltipArgs): React.ReactNode => {
  if (!cashSymbol || !commodity) {
    return '';
  }

  const textLine1 = `CME ${commodity || ''} Cash (${cashSymbol})`;
  const textLine2 = cashTimestamp
    ? `${dayjs(cashTimestamp).format('YYYY-MM-DD')} Close Price`
    : '';
  return (
    <>
      {textLine1} <br />
      {textLine2}
    </>
  );
};

type FuturesPriceTooltipArgs = {
  commodity?: string;
  futuresSymbol?: string;
  futuresTimestamp?: string | null;
};

export const generateFuturesPriceTooltip = ({
  commodity,
  futuresSymbol,
  futuresTimestamp,
}: FuturesPriceTooltipArgs): React.ReactNode => {
  if (!commodity || !futuresSymbol) {
    return '';
  }

  const textLine1 = `CME ${commodity || ''} Futures (${futuresSymbol})`;
  const textLine2 = futuresTimestamp
    ? `${dayjs(futuresTimestamp).format('YYYY-MM-DD')} Close Price`
    : '';
  return (
    <>
      {textLine1} <br />
      {textLine2}
    </>
  );
};

export const cashPriceColumn: ColumnDef = {
  id: 'cashPrice',
  label: (
    <InfoTooltip
      title={
        <>
          Latest relevant cash value. See the{' '}
          <a
            href={`${pages.faq.path}?faqs=what-are-the-calculations-for-actual-ending-value-and-expected-ending-value`}
            target="_blank"
            rel="noreferrer"
          >
            FAQ
          </a>{' '}
          for more detail.
        </>
      }
    >
      <span>Cash Price</span>
    </InfoTooltip>
  ),
  render: (
    cashPrice,
    { actualEndingValueTotal, cashSymbol, cashTimestamp, commodity }
  ) => {
    if (!cashPrice || actualEndingValueTotal) {
      return '-';
    }
    return (
      <Tooltip
        title={generateCashPriceTooltip({
          cashSymbol: cashSymbol as string,
          cashTimestamp: cashTimestamp as string | null,
          commodity: commodity as string,
        })}
      >
        <span>{formatCurrency(cashPrice as number)}</span>
      </Tooltip>
    );
  },
};

export const futuresPriceColumn: ColumnDef = {
  id: 'futuresPrice',
  label: 'Futures Price',
  render: (
    futuresPrice,
    { actualEndingValueTotal, commodity, futuresSymbol, futuresTimestamp }
  ) => {
    if (!futuresPrice || actualEndingValueTotal) {
      return '-';
    }
    return (
      <Tooltip
        title={generateFuturesPriceTooltip({
          commodity: commodity as string,
          futuresSymbol: futuresSymbol as string,
          futuresTimestamp: futuresTimestamp as string | null,
        })}
      >
        <span>{formatCurrency(futuresPrice as number)}</span>
      </Tooltip>
    );
  },
};

export const actualEndingValueColumn: ColumnDef = {
  id: 'actualEndingValue',
  label: 'Actual Ending Value',
  render: (actualEndingValue) => {
    return actualEndingValue
      ? formatCurrency(actualEndingValue as number)
      : '-';
  },
};

export const cashIndemnityColumn: ColumnDef = {
  id: 'cashIndemnity',
  label: (
    <InfoTooltip title={projectedIndemnityTooltipTitle}>
      <span>Projected Indemnity</span>
    </InfoTooltip>
  ),
  render: (indemnity, coverage) => {
    const projectedIndemnity = (coverage.actualIndemnity ??
      coverage.cashIndemnity) as number;
    return <span>{formatCurrency(projectedIndemnity)}</span>;
  },
};

export const futuresIndemnityColumn: ColumnDef = {
  id: 'futuresIndemnity',
  label: (
    <InfoTooltip title={projectedIndemnityTooltipTitle}>
      <span>Projected Indemnity</span>
    </InfoTooltip>
  ),
  render: (indemnity, coverage) => {
    const projectedIndemnity = (coverage.actualIndemnity ??
      coverage.futuresIndemnity) as number;
    return <span>{formatCurrency(projectedIndemnity)}</span>;
  },
};

export const lgmProjectedOrActualIndemnityColumn: ColumnDef = {
  id: 'lgmProjectIndemnity',
  label: (
    <InfoTooltip title={projectedIndemnityTooltipTitle}>
      <span>Projected Indemnity</span>
    </InfoTooltip>
  ),
  render: (_, { lgmProjectedIndemnity, lgmActualIndemnity }) => {
    return (
      <span>
        {formatCurrency(
          (lgmActualIndemnity ?? lgmProjectedIndemnity) as number
        )}
      </span>
    );
  },
};
